<template>
  <v-app-bar
    app
    flat
    color="background"
    style="z-index: 10"
    :height="deviceSize.mobile ? '54px' : '66px'"
    class="main-nav-bar"
    :class="{ mobile: deviceSize.mobile }"
  >
    <v-fade-transition
      group
      leave-absolute
      tag="div"
      class="d-flex align-center justify-space-between"
      style="width: 100% !important; padding: 6px 4px !important"
      duration="50"
    >
      <v-avatar
        v-if="deviceSize.mobile"
        tile
        class="rounded-lg d-flex grow"
        key="wordmark"
        style="width: 150px !important; max-width: 150px !important"
      >
        <v-img
          lazy-src="/img/wordmark_filled.svg"
          max-height="40px"
          max-width="150px"
          src="/img/wordmark_filled.svg"
          aspect-ratio="1"
        ></v-img>
      </v-avatar>
      <v-fade-transition
        tag="div"
        hide-on-leave
        group
        key="first-group"
        class="d-flex align-center"
        :class="{ 'justify-start': $vuetify.breakpoint.smAndUp }"
        style="transition-delay: 75ms !important; width: 100% !important"
        duration="50"
      >
        <StyledButton
          v-if="$vuetify.breakpoint.smAndUp && !miniNav"
          preset="action-stacked"
          icon="mdi-menu-open"
          color="background"
          textColor="icon"
          :dense="true"
          minWidth="48px"
          height="36px"
          label="Menu"
          rounded="lg"
          key="nav-close"
          :action="() => $emit('toggle-nav')"
          :text-transform="true"
          class="px-1"
        />
        <ais-instant-search
          v-if="$vuetify.breakpoint.smAndUp || algoliaSearchSheet"
          :search-client="algolia"
          :index-name="algoliaAccountsIndexName"
          :initial-ui-state="initialUiState"
          :future="{
            preserveSharedStateOnUnmount: true,
          }"
          key="search"
          class="d-flex"
          :class="algoliaInstantSearchClass"
          :style="algoliaInstantSearchStyle"
          v-click-outside="handleAlgoliaInstantSearchClickOutside"
        >
          <ais-configure :hits-per-page.camel="algoliaHitsPerPage" />
          <template v-if="$vuetify.breakpoint.smAndUp">
          <v-fade-transition
            tag="div"
            class="d-flex align-center justify-end shrink"
            style="
              gap: 8px;
              width: 100% !important;
              transition: all 0.1s ease !important;
            "
            leave-absolute
            hide-on-leave
            group
            key="search-group"
            id="algolia"
          >
            <v-menu
              v-model="menu"
              transition="slide-y-transition"
              ref="menu"
              key="menu"
              offset-y
              bottom
              origin="top right"
              :nudge-bottom="12"
              :close-on-content-click="false"
              close-on-click
            >
              <template v-slot:activator="{ on, attrs }">
                <ais-search-box key="search-box" style="width: 100% !important">
                  <template
                    v-slot="{ currentRefinement, isSearchStalled, refine }"
                  >
                    <v-text-field
                      v-bind="attrs"
                      v-on="on"
                      @input="handleAlgoliaInput(refine, currentRefinement)"
                      @keyup.esc="closeAlgoliaSearchMenu"
                      @blur="onAlgoliaSearchBlur"
                      autocomplete="off"
                      v-model="currentRefinement"
                      hide-details
                      rounded
                      dense
                      clearable
                      full-width
                      ref="algolia"
                      id="algolia-search"
                      prepend-inner-icon="mdi-magnify"
                      class="algolia-search secondaryBackground"
                      @keydown="
                        handleKeyboardSearchNavigation(null, null, $event)
                      "
                      style="width: 100% !important"
                      :style="{
                        border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                      }"
                      tabindex="0"
                    >
                      <template v-slot:label>
                        <div
                          style="
                            height: 100%;
                            width: 100% !important;
                            min-width: 200px !important;
                            min-height: 22px !important;
                          "
                        >
                          <span class="mr-2">{{ algoliaSearchLabel }}</span>
                          <kbd
                            v-if="!deviceSize.mobile"
                            class="sheet secondary primary--text"
                            style="
                              margin-top: -2px !important;
                              border-radius: 6px !important;
                            "
                          >
                            <span
                              class="font-weight-bold text-body-2 d-inline-flex align-center"
                              style="line-height: normal !important"
                            >
                              <v-icon size="10" class="mr-1 primary--text">{{
                                metaKey.icon
                              }}</v-icon
                              >{{
                                keyboardShortcuts.accountSearch.keys[1]
                              }}</span
                            >
                          </kbd>
                        </div>
                      </template>
                    </v-text-field>
                  </template>
                </ais-search-box>
              </template>
              <v-card
                class="rounded-lg d-flex flex-column grow algolia-search-card"
                style="width: clamp(250px, 100%, 100%) !important"
              >
                <v-tabs
                  v-if="hasAlgoliaOrdersIndex"
                  v-model="algoliaSearchScopeTabModel"
                  :background-color="$vuetify.theme.currentTheme.background"
                  class="algolia-search-scope-tabs flex-grow-0"
                  grow
                  height="44"
                  slider-color="review"
                >
                  <v-tab class="text-capitalize text-body-2 font-weight-medium">
                    All
                  </v-tab>
                  <v-tab class="text-capitalize text-body-2 font-weight-medium">
                    Accounts
                  </v-tab>
                  <v-tab class="text-capitalize text-body-2 font-weight-medium">
                    Orders
                  </v-tab>
                </v-tabs>
                <v-divider v-if="hasAlgoliaOrdersIndex" class="my-0" />
                <div class="algolia-search-results-list pt-0" style="width: 100%">
                  <template v-if="showAccountSearchSection">
                    <v-sheet
                      class="algolia-search-section-head secondaryBackground d-flex align-center justify-space-between px-3 py-2 flex-wrap"
                      tag="div"
                    >
                      <div class="d-flex align-center min-width-0 py-1">
                        <v-icon
                          class="mr-2 flex-shrink-0"
                          color="review"
                          small
                        >
                          mdi-account-multiple-outline
                        </v-icon>
                        <span
                          class="text-overline font-weight-bold text--secondary"
                          style="letter-spacing: 0.08em"
                        >
                          Accounts
                        </span>
                      </div>
                      <ais-state-results>
                        <!-- Vue 2: slot must always render a root node; if the only child is v-if=false, AisStateResults shows its default help text -->
                        <template v-slot="{ results }">
                          <div class="ais-state-results-toolbar-slot d-flex justify-end align-center">
                            <div
                              v-if="results && !loading && results.nbPages > 1"
                              class="algolia-search-pagination-inline d-flex align-center justify-end flex-shrink-0"
                            >
                              <ais-pagination
                                :show-first="false"
                                :show-last="false"
                                :padding="2"
                              >
                                <template
                                  v-slot="{
                                    currentRefinement,
                                    nbPages,
                                    refine,
                                  }"
                                >
                                  <div
                                    class="algolia-search-pagination--order-summary d-flex align-center flex-nowrap"
                                    style="gap: 20px"
                                  >
                                    <span
                                      class="text-caption font-weight-medium text-end text--secondary"
                                    >
                                      Page {{ currentRefinement + 1 }} of {{ nbPages }}
                                    </span>
                                    <div class="d-flex align-center" style="gap: 8px">
                                      <v-btn
                                        class="text-caption font-weight-bold"
                                        :min-height="
                                          $vuetify.breakpoint.smAndDown
                                            ? '30px'
                                            : '40px'
                                        "
                                        outlined
                                        small
                                        depressed
                                        text
                                        color="review"
                                        style="text-transform: none !important"
                                        :disabled="currentRefinement <= 0"
                                        @click="refine(currentRefinement - 1)"
                                      >
                                        <v-icon>$back</v-icon>
                                      </v-btn>
                                      <v-btn
                                        class="text-caption font-weight-bold"
                                        :min-height="
                                          $vuetify.breakpoint.smAndDown
                                            ? '30px'
                                            : '40px'
                                        "
                                        outlined
                                        small
                                        depressed
                                        text
                                        color="review"
                                        style="text-transform: none !important"
                                        :disabled="currentRefinement + 1 >= nbPages"
                                        @click="refine(currentRefinement + 1)"
                                      >
                                        <v-icon>$next</v-icon>
                                      </v-btn>
                                    </div>
                                  </div>
                                </template>
                              </ais-pagination>
                            </div>
                          </div>
                        </template>
                      </ais-state-results>
                    </v-sheet>
                    <v-list class="pa-0" style="width: 100%">
                    <ais-hits>
                    <template v-slot="{ items, sendEvent }">
                      <v-list-item-group v-model="accountSearch">
                        <v-list-item
                          v-if="loading"
                          key="noData"
                          class="d-flex justify-center align-center"
                        >
                          <div class="d-flex justify-center">
                            <div
                              class="d-flex flex-row align-center justify-center"
                            >
                              <v-progress-circular
                                :width="3"
                                size="15"
                                color="loading"
                                indeterminate
                              ></v-progress-circular>
                              <v-list-item-subtitle class="text--disabled ml-2"
                                >Loading results...</v-list-item-subtitle
                              >
                            </div>
                          </div>
                        </v-list-item>
                        <v-list-item
                          v-else-if="algoliaQueryTooShort"
                          key="query-too-short"
                        >
                          <v-list-item-content>
                            <v-list-item-subtitle class="text-center">
                              <span class="text-wrap text--secondary"
                                >Type at least
                                {{ algoliaMinQueryLength }} characters to
                                search.</span
                              >
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-else-if="items.length === 0">
                          <v-list-item-content>
                            <v-list-item-subtitle class="text-center">
                              <span class="text-wrap">No records found.</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <span
                          v-else
                          v-for="(item, i) in items"
                          :key="item.objectID"
                        >
                          <v-divider
                            v-if="i !== 0"
                            :key="`${i}-divider`"
                          ></v-divider>
                          <AccountCell
                            :account="item"
                            :key="item.objectID"
                            :value="item.objectID"
                            :fetch-logo="fetchLogo"
                            show-status
                            show-actions
                            show-open-new-tab
                            @keydown-emit="
                              (event) =>
                                handleKeyboardSearchNavigation(i, items, event)
                            "
                            @close="onAlgoliaSearchClose"
                            ref="searchResult"
                          />
                        </span>
                      </v-list-item-group>
                    </template>
                  </ais-hits>
                    </v-list>
                  </template>
                  <template v-if="showOrderSearchSection">
                    <v-divider
                      v-if="showAccountSearchSection && showOrderSearchSection"
                      class="my-0"
                    />
                    <ais-index
                      :index-name="algoliaOrdersIndexName"
                      index-id="orders-desktop"
                    >
                      <ais-configure :hits-per-page.camel="algoliaOrderHitsPerPage" />
                      <v-sheet
                        class="algolia-search-section-head secondaryBackground d-flex align-center justify-space-between px-3 py-2 flex-wrap"
                        tag="div"
                      >
                        <div class="d-flex align-center min-width-0 py-1">
                          <v-icon
                            class="mr-2 flex-shrink-0"
                            color="review"
                            small
                          >
                            mdi-receipt-text-outline
                          </v-icon>
                          <span
                            class="text-overline font-weight-bold text--secondary"
                            style="letter-spacing: 0.08em"
                          >
                            Orders
                          </span>
                        </div>
                        <ais-state-results>
                          <template v-slot="{ results }">
                            <div class="ais-state-results-toolbar-slot d-flex justify-end align-center">
                              <div
                                v-if="results && !loading && !algoliaQueryTooShort && results.nbPages > 1"
                                class="algolia-search-pagination-inline d-flex align-center justify-end flex-shrink-0"
                              >
                                <ais-pagination
                                  :show-first="false"
                                  :show-last="false"
                                  :padding="2"
                                >
                                  <template
                                    v-slot="{
                                      currentRefinement,
                                      nbPages,
                                      refine,
                                    }"
                                  >
                                    <div
                                      class="algolia-search-pagination--order-summary d-flex align-center flex-nowrap"
                                      style="gap: 20px"
                                    >
                                      <span
                                        class="text-caption font-weight-medium text-end text--secondary"
                                      >
                                        Page {{ currentRefinement + 1 }} of {{ nbPages }}
                                      </span>
                                      <div class="d-flex align-center" style="gap: 8px">
                                        <v-btn
                                          class="text-caption font-weight-bold"
                                          :min-height="
                                            $vuetify.breakpoint.smAndDown
                                              ? '30px'
                                              : '40px'
                                          "
                                          outlined
                                          small
                                          depressed
                                          text
                                          color="review"
                                          style="text-transform: none !important"
                                          :disabled="currentRefinement <= 0"
                                          @click="refine(currentRefinement - 1)"
                                        >
                                          <v-icon>$back</v-icon>
                                        </v-btn>
                                        <v-btn
                                          class="text-caption font-weight-bold"
                                          :min-height="
                                            $vuetify.breakpoint.smAndDown
                                              ? '30px'
                                              : '40px'
                                          "
                                          outlined
                                          small
                                          depressed
                                          text
                                          color="review"
                                          style="text-transform: none !important"
                                          :disabled="currentRefinement + 1 >= nbPages"
                                          @click="refine(currentRefinement + 1)"
                                        >
                                          <v-icon>$next</v-icon>
                                        </v-btn>
                                      </div>
                                    </div>
                                  </template>
                                </ais-pagination>
                              </div>
                            </div>
                          </template>
                        </ais-state-results>
                      </v-sheet>
                      <v-list class="pa-0" style="width: 100%">
                      <ais-hits>
                        <template v-slot="{ items }">
                          <v-list-item
                            v-if="!loading && !algoliaQueryTooShort && items.length === 0"
                            key="no-order-data"
                          >
                            <v-list-item-content>
                              <v-list-item-subtitle class="text-center text--secondary">
                                No order records found.
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <span v-else v-for="(item, i) in items" :key="'order-' + item.objectID">
                            <v-divider v-if="i !== 0" :key="'order-divider-' + i" />
                            <OrderCell
                              :order="item"
                              show-status
                              @activate="openSalesOrderFromSearch"
                            />
                          </span>
                        </template>
                      </ais-hits>
                      </v-list>
                    </ais-index>
                  </template>
                </div>
              </v-card>
            </v-menu>
          </v-fade-transition>
          </template>

          <template v-else>
            <v-dialog
              v-model="algoliaSearchSheet"
              max-width="560"
              width="92vw"
              scrollable
              transition="dialog-bottom-transition"
              content-class="algolia-mobile-search-dialog"
              key="algolia-search-sheet"
            >
              <v-card class="rounded-lg" style="max-height: 80vh">
                <v-card-title class="py-2 px-3 d-flex align-center">
                  <ais-search-box key="search-box-mobile" style="flex: 1 1 auto; min-width: 0">
                    <template v-slot="{ currentRefinement, refine }">
                      <v-text-field
                        @input="handleAlgoliaInput(refine, currentRefinement)"
                        @keyup.esc="closeAlgoliaMobileSearch"
                        @blur="onAlgoliaSearchBlur"
                        autocomplete="off"
                        v-model="currentRefinement"
                        hide-details
                        rounded
                        dense
                        clearable
                        full-width
                        ref="algolia"
                        id="algolia-search-mobile"
                        prepend-inner-icon="mdi-magnify"
                        :placeholder="algoliaSearchLabel"
                        persistent-placeholder
                        class="algolia-search secondaryBackground"
                        @keydown="
                          handleKeyboardSearchNavigation(null, null, $event)
                        "
                        style="width: 100% !important"
                        :style="{
                          border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                        }"
                        tabindex="0"
                      />
                    </template>
                  </ais-search-box>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="closeAlgoliaMobileSearch"
                    aria-label="Close search"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-0" style="overflow-y: auto; max-height: 70vh">
                  <div
                    v-if="hasAlgoliaOrdersIndex"
                    class="algolia-mobile-search-tabs"
                    :style="{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: $vuetify.theme.currentTheme.background,
                    }"
                  >
                    <v-tabs
                      v-model="algoliaSearchScopeTabModel"
                      :background-color="$vuetify.theme.currentTheme.background"
                      class="algolia-search-scope-tabs"
                      grow
                      height="44"
                      slider-color="review"
                    >
                      <v-tab class="text-capitalize text-body-2 font-weight-medium">
                        All
                      </v-tab>
                      <v-tab class="text-capitalize text-body-2 font-weight-medium">
                        Accounts
                      </v-tab>
                      <v-tab class="text-capitalize text-body-2 font-weight-medium">
                        Orders
                      </v-tab>
                    </v-tabs>
                    <v-divider class="my-0" />
                  </div>
                  <div class="algolia-search-results-list pt-0" style="width: 100%">
                    <template v-if="showAccountSearchSection">
                    <v-sheet
                      class="algolia-search-section-head secondaryBackground d-flex align-center justify-space-between px-3 py-2 flex-wrap"
                      tag="div"
                    >
                      <div class="d-flex align-center min-width-0 py-1">
                        <v-icon
                          class="mr-2 flex-shrink-0"
                          color="review"
                          small
                        >
                          mdi-account-multiple-outline
                        </v-icon>
                        <span
                          class="text-overline font-weight-bold text--secondary"
                          style="letter-spacing: 0.08em"
                        >
                          Accounts
                        </span>
                      </div>
                      <ais-state-results>
                        <template v-slot="{ results }">
                          <div class="ais-state-results-toolbar-slot d-flex justify-end align-center">
                            <div
                              v-if="results && !loading && results.nbPages > 1"
                              class="algolia-search-pagination-inline d-flex align-center justify-end flex-shrink-0"
                            >
                              <ais-pagination
                                :show-first="false"
                                :show-last="false"
                                :padding="2"
                              >
                                <template
                                  v-slot="{
                                    currentRefinement,
                                    nbPages,
                                    refine,
                                  }"
                                >
                                  <div
                                    class="algolia-search-pagination--order-summary d-flex align-center flex-nowrap"
                                    style="gap: 20px"
                                  >
                                    <span
                                      class="text-caption font-weight-medium text-end text--secondary"
                                    >
                                      Page {{ currentRefinement + 1 }} of {{ nbPages }}
                                    </span>
                                    <div class="d-flex align-center" style="gap: 8px">
                                      <v-btn
                                        class="text-caption font-weight-bold"
                                        :min-height="
                                          $vuetify.breakpoint.smAndDown
                                            ? '30px'
                                            : '40px'
                                        "
                                        outlined
                                        small
                                        depressed
                                        text
                                        color="review"
                                        style="text-transform: none !important"
                                        :disabled="currentRefinement <= 0"
                                        @click="refine(currentRefinement - 1)"
                                      >
                                        <v-icon>$back</v-icon>
                                      </v-btn>
                                      <v-btn
                                        class="text-caption font-weight-bold"
                                        :min-height="
                                          $vuetify.breakpoint.smAndDown
                                            ? '30px'
                                            : '40px'
                                        "
                                        outlined
                                        small
                                        depressed
                                        text
                                        color="review"
                                        style="text-transform: none !important"
                                        :disabled="currentRefinement + 1 >= nbPages"
                                        @click="refine(currentRefinement + 1)"
                                      >
                                        <v-icon>$next</v-icon>
                                      </v-btn>
                                    </div>
                                  </div>
                                </template>
                              </ais-pagination>
                            </div>
                          </div>
                        </template>
                      </ais-state-results>
                    </v-sheet>
                    <v-list class="pa-0" style="width: 100%">
                    <ais-hits>
                      <template v-slot="{ items, sendEvent }">
                        <v-list-item-group v-model="accountSearch">
                          <v-list-item
                            v-if="loading"
                            key="noData-mobile"
                            class="d-flex justify-center align-center"
                          >
                            <div class="d-flex justify-center">
                              <div
                                class="d-flex flex-row align-center justify-center"
                              >
                                <v-progress-circular
                                  :width="3"
                                  size="15"
                                  color="loading"
                                  indeterminate
                                ></v-progress-circular>
                                <v-list-item-subtitle class="text--disabled ml-2"
                                  >Loading results...</v-list-item-subtitle
                                >
                              </div>
                            </div>
                          </v-list-item>
                          <v-list-item
                            v-else-if="algoliaQueryTooShort"
                            key="query-too-short-mobile"
                          >
                            <v-list-item-content>
                              <v-list-item-subtitle class="text-center">
                                <span class="text-wrap text--secondary"
                                  >Type at least
                                  {{ algoliaMinQueryLength }} characters to
                                  search.</span
                                >
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item v-else-if="items.length === 0">
                            <v-list-item-content>
                              <v-list-item-subtitle class="text-center">
                                <span class="text-wrap">No records found.</span>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <span
                            v-else
                            v-for="(item, i) in items"
                            :key="'m-' + item.objectID"
                          >
                            <v-divider
                              v-if="i !== 0"
                              :key="`m-${i}-divider`"
                            ></v-divider>
                            <AccountCell
                              :account="item"
                              :key="'m-cell-' + item.objectID"
                              :value="item.objectID"
                              :fetch-logo="fetchLogo"
                              show-status
                              show-actions
                              show-open-new-tab
                              @keydown-emit="
                                (event) =>
                                  handleKeyboardSearchNavigation(i, items, event)
                              "
                              @close="onAlgoliaSearchClose"
                              ref="searchResult"
                            />
                          </span>
                        </v-list-item-group>
                      </template>
                    </ais-hits>
                    </v-list>
                    </template>
                    <template v-if="showOrderSearchSection">
                      <v-divider
                        v-if="showAccountSearchSection && showOrderSearchSection"
                        class="my-0"
                      />
                      <ais-index :index-name="algoliaOrdersIndexName" index-id="orders-mobile">
                        <ais-configure :hits-per-page.camel="algoliaOrderHitsPerPage" />
                        <v-sheet
                          class="algolia-search-section-head secondaryBackground d-flex align-center justify-space-between px-3 py-2 flex-wrap"
                          tag="div"
                        >
                          <div class="d-flex align-center min-width-0 py-1">
                            <v-icon
                              class="mr-2 flex-shrink-0"
                              color="review"
                              small
                            >
                              mdi-receipt-text-outline
                            </v-icon>
                            <span
                              class="text-overline font-weight-bold text--secondary"
                              style="letter-spacing: 0.08em"
                            >
                              Orders
                            </span>
                          </div>
                          <ais-state-results>
                            <template v-slot="{ results }">
                              <div class="ais-state-results-toolbar-slot d-flex justify-end align-center">
                                <div
                                  v-if="results && !loading && !algoliaQueryTooShort && results.nbPages > 1"
                                  class="algolia-search-pagination-inline d-flex align-center justify-end flex-shrink-0"
                                >
                                  <ais-pagination
                                    :show-first="false"
                                    :show-last="false"
                                    :padding="2"
                                  >
                                    <template
                                      v-slot="{
                                        currentRefinement,
                                        nbPages,
                                        refine,
                                      }"
                                    >
                                      <div
                                        class="algolia-search-pagination--order-summary d-flex align-center flex-nowrap"
                                        style="gap: 20px"
                                      >
                                        <span
                                          class="text-caption font-weight-medium text-end text--secondary"
                                        >
                                          Page {{ currentRefinement + 1 }} of {{ nbPages }}
                                        </span>
                                        <div class="d-flex align-center" style="gap: 8px">
                                          <v-btn
                                            class="text-caption font-weight-bold"
                                            :min-height="
                                              $vuetify.breakpoint.smAndDown
                                                ? '30px'
                                                : '40px'
                                            "
                                            outlined
                                            small
                                            depressed
                                            text
                                            color="review"
                                            style="text-transform: none !important"
                                            :disabled="currentRefinement <= 0"
                                            @click="refine(currentRefinement - 1)"
                                          >
                                            <v-icon>$back</v-icon>
                                          </v-btn>
                                          <v-btn
                                            class="text-caption font-weight-bold"
                                            :min-height="
                                              $vuetify.breakpoint.smAndDown
                                                ? '30px'
                                                : '40px'
                                            "
                                            outlined
                                            small
                                            depressed
                                            text
                                            color="review"
                                            style="text-transform: none !important"
                                            :disabled="currentRefinement + 1 >= nbPages"
                                            @click="refine(currentRefinement + 1)"
                                          >
                                            <v-icon>$next</v-icon>
                                          </v-btn>
                                        </div>
                                      </div>
                                    </template>
                                  </ais-pagination>
                                </div>
                              </div>
                            </template>
                          </ais-state-results>
                        </v-sheet>
                        <v-list class="pa-0" style="width: 100%">
                        <ais-hits>
                          <template v-slot="{ items }">
                            <v-list-item
                              v-if="!loading && !algoliaQueryTooShort && items.length === 0"
                              key="no-order-data-mobile"
                            >
                              <v-list-item-content>
                                <v-list-item-subtitle class="text-center text--secondary">
                                  No order records found.
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                            <span v-else v-for="(item, i) in items" :key="'m-order-' + item.objectID">
                              <v-divider v-if="i !== 0" :key="'m-order-divider-' + i" />
                              <OrderCell
                                :order="item"
                                show-status
                                @activate="openSalesOrderFromSearch"
                              />
                            </span>
                          </template>
                        </ais-hits>
                        </v-list>
                      </ais-index>
                    </template>
                  </div>
                </v-card-text>
              </v-card>
            </v-dialog>
          </template>
        </ais-instant-search>
      </v-fade-transition>
      <div
        class="d-flex align-center mr-1"
        v-if="$vuetify.breakpoint.smAndUp || !searchVisible"
        key="actions"
        style="gap: 24px"
      >
        <template v-if="$vuetify.breakpoint.smAndUp">
          <div
            v-if="downloadingUpdate"
            class="text-caption font-weight-bold text--secondary d-flex align-center justify-center text-no-wrap"
            style="gap: 6px"
            key="updates"
          >
            <v-progress-circular
              :width="2"
              size="10"
              color="loading"
              indeterminate
            ></v-progress-circular>
            Downloading Updates
          </div>
          <StyledButton
            v-else-if="updateExists"
            preset="outlined"
            label="Install Updates"
            icon="mdi-download"
            iconSize="14px"
            minHeight="36px"
            size="small"
            rounded="pill"
            key="updates-available"
            :action="onInstallUpdate"
          />
        </template>
        <div class="d-flex align-center" style="gap: 12px">
          <v-icon
            v-if="deviceSize.mobile && !searchVisible"
            color="icon"
            :size="deviceSize.mobile ? 24 : 28"
            @click.stop="handleSearchClick"
            >mdi-magnify</v-icon
          >
          <v-icon
            v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
            color="icon"
            :size="deviceSize.mobile ? 24 : 28"
            @click="openSupportTicket"
            >mdi-help-circle-outline</v-icon
          >
          <v-badge
            v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
            overlap
            bordered
            color="error"
            :content="alerts"
            :value="alerts"
          >
            <v-icon
              @click="viewNotifications"
              :size="deviceSize.mobile ? 24 : 28"
              >mdi-bell-outline</v-icon
            >
          </v-badge>
        </div>
        <v-menu
          v-if="!searchVisible || $vuetify.breakpoint.smAndUp"
          bottom
          min-width="200px"
          class="rounded-lg"
          offset-y
          :nudge-bottom="6"
          key="menu"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              :height="deviceSize.mobile ? 36 : 40"
              :width="deviceSize.mobile ? 36 : 40"
              v-on="on"
              key="menu-activator"
            >
              <v-avatar color="review" :size="deviceSize.mobile ? 36 : 40">
                <span
                  class="text-uppercase font-weight-medium white--text"
                  :style="`font-size: 0.95rem;
                            letter-spacing: 0.0125em;`"
                >
                  {{ getUser.initials }}
                </span>
              </v-avatar>
            </v-btn>
          </template>
          <v-card class="rounded-lg">
            <v-list-item-content class="justify-center pb-0 rounded-lg">
              <div class="mx-auto text-center rounded-lg">
                <v-avatar color="review" class="my-2">
                  <span class="text-uppercase text-h6 white--text">
                    {{ getUser.initials }}
                  </span>
                </v-avatar>
                <h3 class="text-capitalize">
                  {{ `${getUser?.name?.first} ${getUser?.name?.last}` }}
                </h3>
                <p class="text-caption">
                  <span>{{ getUser.email }}</span>
                  <span class="text-caption d-block">
                    {{
                      getUser.phoneNumber?.length > 0
                        ? $options.filters.phoneNumber(getUser.phoneNumber)
                        : "-"
                    }}
                  </span>
                </p>
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex align-center pl-4">
                    <v-icon left small> mdi-theme-light-dark </v-icon>
                    <span
                      class="text-body-2 font-weight-medium text--secondary d-flex text-center justify-center align-center"
                      >Dark Mode</span
                    >
                  </div>
                  <v-switch
                    hide-details
                    :input-value="$vuetify.theme.dark"
                    color="review"
                    inset
                    dense
                    class="mt-0 pt-0"
                    @change="toggleTheme"
                    @click.stop
                  ></v-switch>
                </div>
                <v-btn
                  v-if="getUser.accountType >= 2"
                  depressed
                  block
                  text
                  @click="traverse('profile-view')"
                >
                  <v-icon small left>mdi-account</v-icon>
                  <span class="text-capitalize">profile</span>
                </v-btn>
                <p class="text-caption mt-2 mb-0" style="opacity: 0.6">
                  {{ getAppName }} v26.4.01
                </p>
                <v-divider class="mt-3"></v-divider>
                <v-btn
                  depressed
                  block
                  text
                  class="rounded-b-lg py-6"
                  @click="$emit('logout')"
                >
                  <v-icon small left>mdi-logout</v-icon>
                  <span class="text-capitalize">log out</span>
                </v-btn>
              </div>
            </v-list-item-content>
          </v-card>
        </v-menu>
      </div>
    </v-fade-transition>
  </v-app-bar>
</template>

<script>
// Libraries
import { mapGetters, mapActions } from "vuex";
import algoliasearch from "algoliasearch/lite";
import {
  // Firestore
  firestore,
  getDoc,
  doc,
  // Storage
  storage,
  ref,
  getDownloadURL,
} from "@/firebase/init";
// Components

const themeChannel = new BroadcastChannel("theme-channel");

/** Skip Algolia network calls for empty / very short queries (saves search operations on focus and while typing). */
const ALGOLIA_MIN_QUERY_LENGTH = 2;

/** Hits per Algolia page; extra pages use ais-pagination (separate search per page). */
const ALGOLIA_APPBAR_HITS_PER_PAGE = 5;
const ALGOLIA_APPBAR_ORDER_HITS_PER_PAGE = 5;

function emptyAlgoliaIndexResult(request) {
  const params = request.params || {};
  return {
    hits: [],
    nbHits: 0,
    nbPages: 0,
    page: params.page ?? 0,
    hitsPerPage: params.hitsPerPage ?? 20,
    processingTimeMS: 0,
    exhaustiveNbHits: true,
    query: params.query ?? "",
    params: "",
  };
}

/**
 * Wraps the lite client so `search` never hits the network until the query is long enough.
 * Merges stub results back in the same order as `requests` (supports multi-index if added later).
 */
function createGuardedAlgoliaSearchClient(baseClient) {
  return {
    ...baseClient,
    search(requests) {
      if (!requests || !requests.length) {
        return Promise.resolve({ results: [] });
      }
      const skipMask = requests.map((request) => {
        const q = String(request.params?.query ?? "").trim();
        return q.length < ALGOLIA_MIN_QUERY_LENGTH;
      });
      if (skipMask.every(Boolean)) {
        return Promise.resolve({
          results: requests.map((request) => emptyAlgoliaIndexResult(request)),
        });
      }
      const fetchRequests = requests.filter((_, i) => !skipMask[i]);
      return baseClient.search(fetchRequests).then((response) => {
        if (fetchRequests.length === requests.length) {
          return response;
        }
        const merged = [];
        let j = 0;
        for (let i = 0; i < requests.length; i++) {
          merged.push(
            skipMask[i]
              ? emptyAlgoliaIndexResult(requests[i])
              : response.results[j++],
          );
        }
        return { results: merged };
      });
    },
  };
}

export default {
  name: "AppBar",
  data() {
    const algoliaAccountsIndexName =
      String(process.env.VUE_APP_ALGOLIA_ACCOUNTS_INDEX ?? "").trim() ||
      "mytrace";

    return {
      searchVisible: false,
      /** Mobile: Algolia account search bottom sheet. */
      algoliaSearchSheet: false,
      menu: false,
      loading: false,
      debouncedRefineSearch: null,
      accountSearch: null,
      algoliaSearchScope: "all",
      /** Mirrors the search field for list hints (short-query message). */
      algoliaSearchDraft: "",
      /** Must match ALGOLIA_MIN_QUERY_LENGTH in the guarded client wrapper. */
      algoliaMinQueryLength: ALGOLIA_MIN_QUERY_LENGTH,
      focusedSearchIndex: 0,
      algoliaHitsPerPage: ALGOLIA_APPBAR_HITS_PER_PAGE,
      algoliaOrderHitsPerPage: ALGOLIA_APPBAR_ORDER_HITS_PER_PAGE,
      algoliaAccountsIndexName,
      /** Optional; leave env unset until the sales orders Algolia index exists. */
      algoliaOrdersIndexName: String(
        process.env.VUE_APP_ALGOLIA_SALES_ORDERS_INDEX ?? "",
      ).trim(),
      algolia: createGuardedAlgoliaSearchClient(
        algoliasearch(
          process.env.VUE_APP_ALGOLIA_APPID,
          process.env.VUE_APP_ALGOLIA_API,
        ),
      ),
      initialUiState: {
        [algoliaAccountsIndexName]: {
          query: "",
        },
      },
    };
  },
  props: {
    downloadingUpdate: {
      default: false,
      type: Boolean,
      required: true,
    },
    updateExists: {
      default: false,
      type: Boolean,
      required: true,
    },
    miniNav: {
      default: false,
      type: Boolean,
      required: true,
    },
    alerts: {
      default: 0,
      type: Number,
      required: true,
    },
  },
  methods: {
    ...mapActions([
      "_user",
      "_activityDialog",
      "_notification",
      "_email",
      "_dialpadFeatureFlag",
      "_rightDrawer",
      "_alerts",
    ]),
    onInstallUpdate() {
      this.$emit("install-update");
    },
    /* API */
    // GET
    async fetchLogo(accountID, cacheManager) {
      try {
        const cachedLogo = cacheManager.get(accountID);
        if (cachedLogo || cacheManager.hasKey(accountID)) {
          return cachedLogo ?? null; // Use cached logo if available
        }
        // Fetch logo details from Firestore
        const accountsDoc = doc(firestore, "accounts", accountID);
        const response = await getDoc(accountsDoc);
        if (!response.exists() || !response.data().logo) {
          cacheManager.set(accountID, null);
          return null;
        }

        const mediaDoc = doc(firestore, "media", response.data().logo);
        const mediaResponse = await getDoc(mediaDoc);
        if (!mediaResponse.exists()) {
          cacheManager.set(accountID, null);
          return null;
        }

        const path = mediaResponse.data().path;
        const result = await getDownloadURL(ref(storage, path));

        cacheManager.set(accountID, result);
        return result;
      } catch (error) {
        console.error("Error fetching logo:", error);
        return null;
      }
    },
    // POST
    /* */
    handleSearchClick() {
      this.searchVisible = true;
      if (this.$vuetify.breakpoint.smAndDown) {
        this.algoliaSearchSheet = true;
      }
      this.$nextTick(() => {
        this.$nextTick(() => {
          const field = this.$refs.algolia;
          if (field?.focus) field.focus();
          else field?.$refs?.input?.focus?.();
        });
      });
    },
    handleAlgoliaInstantSearchClickOutside() {
      if (this.$vuetify.breakpoint.smAndUp) {
        this.blurSearch();
      }
    },
    closeAlgoliaMobileSearch() {
      this.algoliaSearchSheet = false;
      this.searchVisible = false;
      this.menu = false;
    },
    onAlgoliaSearchBlur() {
      if (this.$vuetify.breakpoint.smAndUp) {
        this.blurSearch();
      }
    },
    onAlgoliaSearchClose() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.closeAlgoliaMobileSearch();
      } else {
        this.closeAlgoliaSearchMenu();
      }
    },
    closeAlgoliaSearchMenu() {
      this.menu = false;
    },
    getOrderHitId(orderHit) {
      if (!orderHit || typeof orderHit !== "object") return "";
      return String(
        orderHit.id ||
          orderHit.objectID ||
          orderHit.salesOrderId ||
          orderHit.orderId ||
          "",
      ).trim();
    },
    openSalesOrderFromSearch(orderHit) {
      const id = this.getOrderHitId(orderHit);
      if (!id) {
        console.error("Unable to open sales order from Algolia hit:", orderHit);
        return;
      }
      this.onAlgoliaSearchClose();
      this.$router.push({
        name: "salesOrder-view",
        params: { id },
      });
    },
    /**
     * Debounced refine: guarded client skips real HTTP until algoliaMinQueryLength,
     * so focus / 1-character typing does not consume Algolia search operations.
     */
    handleAlgoliaInput(refine, currentRefinement) {
      const raw = currentRefinement ?? "";
      this.algoliaSearchDraft = raw;

      if (this.debouncedRefineSearch?.cancel) {
        this.debouncedRefineSearch.cancel();
      }

      const trimmed = String(raw).trim();

      if (raw.length > 0) {
        this.menu = true;
        this.loading = trimmed.length >= this.algoliaMinQueryLength;

        this.debouncedRefineSearch = _.debounce(async () => {
          await refine(raw);
          this.loading = false;
        }, 500);

        this.debouncedRefineSearch();
      } else {
        refine("");
        this.menu = false;
        this.loading = false;
      }
    },
    blurSearch() {
      if (this.deviceSize.mobile && this.searchVisible) {
        this.menu = false;
      }
    },
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem(
        "theme",
        this.$vuetify.theme.dark ? "dark" : "light",
      );
      themeChannel.postMessage({
        theme: this.$vuetify.theme.dark ? "dark" : "light",
      });
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      metaThemeColor.setAttribute(
        "content",
        this.$vuetify.theme.currentTheme.background,
      );
    },
    async handleKeyboardSearchNavigation(i, items, event) {
      let isArrowKey = ["ArrowDown", "ArrowUp"].includes(event.key);
      if (isArrowKey) {
        // Prevent default scrolling behavior
        event.preventDefault();
        event.stopPropagation();

        if (event.key === "ArrowDown") {
          // Increment index, but keep it within bounds
          this.focusedSearchIndex = !items?.length
            ? 0
            : (this.focusedSearchIndex + 1) % this.$refs.searchResult.length;
        } else if (event.key === "ArrowUp") {
          // Decrement index, wrap around if necessary
          this.focusedSearchIndex = !items?.length
            ? this.$refs.searchResult.length - 1
            : (this.focusedSearchIndex - 1 + this.$refs.searchResult.length) %
              this.$refs.searchResult.length;
        }
        let foundItem = null;
        if (items?.length) {
          foundItem = this.$refs.searchResult.find(
            (result) =>
              result.account?.objectID ===
              items[this.focusedSearchIndex].objectID,
          );
        } else {
          foundItem = this.$refs.searchResult[0];
        }
        this.$nextTick(() => {
          let navItem =
            foundItem ?? this.$refs.searchResult[this.focusedSearchIndex];
          if (navItem?.$el) {
            navItem.$el.focus();
          }
        });
      }
    },
    openSupportTicket() {
      window.open(
        "https://mytrace-support-tickets.web.app/",
        "_blank",
        "noopener,noreferrer",
      );
    },
    viewNotifications() {
      this._rightDrawer({
        active: true,
        title: "Notifications",
        type: "notifications",
        data: {},
      });
    },
    async traverse(action, params) {
      let link,
        relatedToResource = "";
      switch (action) {
        case "profile-view":
          this.$router.push({
            name: "profile-view",
            params: {
              id: this.getUser.uid,
            },
          });
          break;
      }
    },
    /**
     * If the sales orders index is not configured, "orders" scope would hide both
     * account hits and order hits. Reset to "all" so accounts-only Algolia still works.
     */
    normalizeAlgoliaSearchScopeForAvailableIndices() {
      if (!this.hasAlgoliaOrdersIndex && this.algoliaSearchScope === "orders") {
        this.algoliaSearchScope = "all";
      }
    },
  },
  computed: {
    ...mapGetters([
      // Main
      "getAppName",
      "getUser",
    ]),
    algoliaQueryTooShort() {
      const q = String(this.algoliaSearchDraft ?? "").trim();
      return q.length > 0 && q.length < this.algoliaMinQueryLength;
    },
    algoliaSearchLabel() {
      return "Search";
    },
    hasAlgoliaOrdersIndex() {
      return this.algoliaOrdersIndexName.length > 0;
    },
    showAccountSearchSection() {
      return this.algoliaSearchScope !== "orders";
    },
    showOrderSearchSection() {
      return this.hasAlgoliaOrdersIndex && this.algoliaSearchScope !== "accounts";
    },
    /** v-tabs v-model (0–2) ↔ algoliaSearchScope string */
    algoliaSearchScopeTabModel: {
      get() {
        const map = { all: 0, accounts: 1, orders: 2 };
        return map[this.algoliaSearchScope] ?? 0;
      },
      set(v) {
        const scopes = ["all", "accounts", "orders"];
        const idx = Number(v);
        if (!this.hasAlgoliaOrdersIndex && idx === 2) {
          this.algoliaSearchScope = "all";
          return;
        }
        this.algoliaSearchScope = scopes[idx] ?? "all";
      },
    },
    algoliaInstantSearchClass() {
      const smAndUp = this.$vuetify.breakpoint.smAndUp;
      return {
        shrink: this.deviceSize.mobile && smAndUp,
        "justify-start": !this.deviceSize.mobile,
        "justify-end": this.deviceSize.mobile,
        "algolia-mobile-mount": !smAndUp && this.algoliaSearchSheet,
      };
    },
    algoliaInstantSearchStyle() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return { width: "clamp(325px, 85%, 500px) !important" };
      }
      return {};
    },
  },
  watch: {
    hasAlgoliaOrdersIndex(val) {
      if (!val) {
        this.normalizeAlgoliaSearchScopeForAvailableIndices();
      }
    },
    algoliaSearchSheet(val) {
      if (!val && this.$vuetify.breakpoint.smAndDown) {
        this.searchVisible = false;
        this.menu = false;
      }
    },
  },
  created() {
    this.normalizeAlgoliaSearchScopeForAvailableIndices();
  },
};
</script>

<style scoped>
.main-nav-bar >>> .v-toolbar__content {
  padding: 6px 8px !important;
}

.main-nav-bar.mobile >>> .v-toolbar__content {
  padding: 6px 0px !important;
}

.algolia-search >>> .v-input__slot {
  padding: 0px 16px !important;
}

.algolia-search >>> .v-text-field__slot {
  align-items: center !important;
  min-height: 100%;
}

.algolia-search >>> * {
  cursor: pointer !important;
}

.algolia-search >>> .v-text-field__slot > label {
  display: flex;
  align-items: center;
  min-height: fit-content !important;
  position: unset !important;
  min-width: fit-content !important;
  padding: 4px 0px !important;
}

/* Algolia search: tabs + section toolbars */
.algolia-search-scope-tabs >>> .v-tabs-bar {
  border-radius: 8px 8px 0 0;
}

/* Keeps layout stable when pagination is hidden (single page) */
.ais-state-results-toolbar-slot {
  min-width: 0;
  min-height: 0;
}

.algolia-search-section-head {
  border-bottom: thin solid rgba(0, 0, 0, 0.08);
}

.theme--dark .algolia-search-section-head {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

/* Mobile search dialog: stack section label + pagination so rows aren’t crushed */
@media (max-width: 600px) {
  .algolia-search-section-head.d-flex {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px;
  }

  .algolia-search-section-head .ais-state-results-toolbar-slot {
    width: 100%;
    justify-content: space-between !important;
  }

  .algolia-search-pagination--order-summary {
    width: 100%;
    justify-content: space-between !important;
  }
}

/* Mobile: zero toolbar footprint; dialog overlays the page */
.algolia-mobile-mount {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 auto !important;
  overflow: visible !important;
}
</style>
